import React, { useState, useEffect } from 'react';
import { 
  Search, Eye, EyeOff, Trash2, Edit3, Save, X, Palette, 
  Type, MapPin, FileText, Loader2, AlertCircle, CheckCircle,
  Copy, ChevronDown, ChevronRight, Code
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config';

const UniversalTextSearch = ({ token, i18n }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedText, setSelectedText] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [textColor, setTextColor] = useState('#000000');
  const [fontSize, setFontSize] = useState('16');
  const [isHidden, setIsHidden] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState({});

  const searchInSite = async () => {
    if (!searchQuery.trim()) {
      toast.error(i18n.language === 'ar' ? 'أدخل نص البحث' : 'Enter search text');
      return;
    }

    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/admin/universal-search`,
        { query: searchQuery },
        { headers }
      );

      setSearchResults(response.data.results || []);
      
      if (response.data.results.length === 0) {
        toast.info(i18n.language === 'ar' ? 'لم يتم العثور على نتائج' : 'No results found');
      } else {
        toast.success(
          i18n.language === 'ar' 
            ? `تم العثور على ${response.data.results.length} نتيجة`
            : `Found ${response.data.results.length} results`
        );
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في البحث' : 'Search error');
    } finally {
      setLoading(false);
    }
  };

  const selectText = (result) => {
    setSelectedText(result);
    setEditingText(result.text);
    setTextColor(result.styles?.color || '#000000');
    setFontSize(result.styles?.fontSize || '16');
    setIsHidden(result.hidden || false);
  };

  const saveChanges = async () => {
    if (!selectedText) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Prepare request data based on whether it's database or file
      const requestData = {
        file: selectedText.file,
        lineNumber: selectedText.lineNumber,
        originalText: selectedText.text,
        newText: editingText,
        styles: {
          color: textColor,
          fontSize: fontSize,
        },
        hidden: isHidden
      };

      // Add database-specific fields if it's a database entry
      if (selectedText.isDatabase) {
        requestData.isDatabase = true;
        requestData.translationKey = selectedText.translationKey;
        requestData.language = selectedText.language;
      }

      await axios.post(
        `${API}/admin/update-text`,
        requestData,
        { headers }
      );

      toast.success(i18n.language === 'ar' ? 'تم الحفظ بنجاح' : 'Saved successfully');
      
      // Update local results
      const updatedResults = searchResults.map(result => 
        result.id === selectedText.id 
          ? { ...result, text: editingText, styles: { color: textColor, fontSize }, hidden: isHidden }
          : result
      );
      setSearchResults(updatedResults);
      setSelectedText(null);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الحفظ' : 'Save error');
    }
  };

  const deleteText = async (result) => {
    if (!confirm(i18n.language === 'ar' ? 'هل تريد حذف هذا النص؟' : 'Delete this text?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/admin/delete-text`,
        {
          file: result.file,
          lineNumber: result.lineNumber,
          text: result.text
        },
        { headers }
      );

      toast.success(i18n.language === 'ar' ? 'تم الحذف' : 'Deleted');
      setSearchResults(searchResults.filter(r => r.id !== result.id));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الحذف' : 'Delete error');
    }
  };

  const toggleVisibility = async (result) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const newHiddenState = !result.hidden;
      
      await axios.post(
        `${API}/admin/toggle-visibility`,
        {
          file: result.file,
          lineNumber: result.lineNumber,
          text: result.text,
          hidden: newHiddenState
        },
        { headers }
      );

      toast.success(
        newHiddenState 
          ? (i18n.language === 'ar' ? 'تم الإخفاء' : 'Hidden')
          : (i18n.language === 'ar' ? 'تم الإظهار' : 'Shown')
      );

      const updatedResults = searchResults.map(r => 
        r.id === result.id ? { ...r, hidden: newHiddenState } : r
      );
      setSearchResults(updatedResults);
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ' : 'Error');
    }
  };

  const toggleFileExpansion = (fileName) => {
    setExpandedFiles(prev => ({
      ...prev,
      [fileName]: !prev[fileName]
    }));
  };

  const groupByFile = () => {
    const grouped = {};
    searchResults.forEach(result => {
      if (!grouped[result.file]) {
        grouped[result.file] = [];
      }
      grouped[result.file].push(result);
    });
    return grouped;
  };

  const groupedResults = groupByFile();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Search className="w-8 h-8" />
          <h2 className="text-2xl font-bold">
            {i18n.language === 'ar' ? 'البحث الشامل في الموقع' : 'Universal Site Search'}
          </h2>
        </div>
        <p className="text-white/90">
          {i18n.language === 'ar' 
            ? 'ابحث عن أي نص في الموقع وتحكم به بالكامل: تعديل، إخفاء، حذف، تغيير اللون والحجم'
            : 'Search any text on the site and control it: edit, hide, delete, change color & size'}
        </p>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              type="text"
              placeholder={i18n.language === 'ar' ? 'ابحث عن نص...' : 'Search for text...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchInSite()}
              className="text-lg"
            />
          </div>
          <Button onClick={searchInSite} disabled={loading} className="gap-2">
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {i18n.language === 'ar' ? 'جاري البحث...' : 'Searching...'}
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                {i18n.language === 'ar' ? 'بحث' : 'Search'}
              </>
            )}
          </Button>
        </div>

        {searchResults.length > 0 && (
          <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-800 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {i18n.language === 'ar' 
                ? `تم العثور على ${searchResults.length} نتيجة في ${Object.keys(groupedResults).length} ملف`
                : `Found ${searchResults.length} results in ${Object.keys(groupedResults).length} files`}
            </p>
          </div>
        )}
      </div>

      {/* Results */}
      {searchResults.length > 0 && (
        <div className="space-y-4">
          {Object.entries(groupedResults).map(([fileName, results]) => (
            <div key={fileName} className="bg-white rounded-lg border overflow-hidden">
              {/* File Header */}
              <div 
                className="bg-slate-100 p-4 flex items-center justify-between cursor-pointer hover:bg-slate-200 transition-colors"
                onClick={() => toggleFileExpansion(fileName)}
              >
                <div className="flex items-center gap-3">
                  {expandedFiles[fileName] ? (
                    <ChevronDown className="w-5 h-5 text-slate-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-600" />
                  )}
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="font-semibold text-slate-900">{fileName}</h3>
                    <p className="text-xs text-slate-500">
                      {results.length} {i18n.language === 'ar' ? 'نتيجة' : 'results'}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="gap-2">
                  <Code className="w-4 h-4" />
                  {i18n.language === 'ar' ? 'فتح الملف' : 'Open File'}
                </Button>
              </div>

              {/* Results in File */}
              {expandedFiles[fileName] && (
                <div className="divide-y">
                  {results.map((result) => (
                    <div key={result.id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            <span className="text-xs text-slate-500">
                              {i18n.language === 'ar' ? 'السطر' : 'Line'} {result.lineNumber}
                            </span>
                            {result.hidden && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded">
                                {i18n.language === 'ar' ? 'مخفي' : 'Hidden'}
                              </span>
                            )}
                          </div>
                          
                          <div 
                            className="p-3 bg-slate-50 rounded border text-sm font-mono mb-2"
                            style={{
                              color: result.styles?.color || '#000000',
                              fontSize: `${result.styles?.fontSize || 16}px`,
                              opacity: result.hidden ? 0.5 : 1
                            }}
                          >
                            {result.text}
                          </div>

                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>{i18n.language === 'ar' ? 'المكون:' : 'Component:'} {result.component}</span>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => selectText(result)}
                            className="gap-2"
                          >
                            <Edit3 className="w-4 h-4" />
                            {i18n.language === 'ar' ? 'تعديل' : 'Edit'}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => toggleVisibility(result)}
                            className="gap-2"
                          >
                            {result.hidden ? (
                              <>
                                <Eye className="w-4 h-4" />
                                {i18n.language === 'ar' ? 'إظهار' : 'Show'}
                              </>
                            ) : (
                              <>
                                <EyeOff className="w-4 h-4" />
                                {i18n.language === 'ar' ? 'إخفاء' : 'Hide'}
                              </>
                            )}
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => deleteText(result)}
                            className="gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            {i18n.language === 'ar' ? 'حذف' : 'Delete'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {selectedText && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold">
                {i18n.language === 'ar' ? 'تعديل النص' : 'Edit Text'}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedText(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              {/* Original Text */}
              <div>
                <Label className="text-xs text-slate-500">
                  {i18n.language === 'ar' ? 'النص الأصلي:' : 'Original Text:'}
                </Label>
                <div className="p-3 bg-slate-100 rounded border text-sm mt-1">
                  {selectedText.text}
                </div>
              </div>

              {/* Edit Text */}
              <div>
                <Label>{i18n.language === 'ar' ? 'النص الجديد' : 'New Text'}</Label>
                <textarea
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  className="w-full p-3 border rounded-lg mt-1"
                  rows="4"
                />
              </div>

              {/* Color Picker */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Palette className="w-4 h-4" />
                    {i18n.language === 'ar' ? 'اللون' : 'Color'}
                  </Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="w-16 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      placeholder="#000000"
                    />
                  </div>
                </div>

                {/* Font Size */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Type className="w-4 h-4" />
                    {i18n.language === 'ar' ? 'الحجم (px)' : 'Size (px)'}
                  </Label>
                  <Input
                    type="number"
                    value={fontSize}
                    onChange={(e) => setFontSize(e.target.value)}
                    min="8"
                    max="72"
                  />
                </div>
              </div>

              {/* Hidden Toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                <Label className="flex items-center gap-2">
                  {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {i18n.language === 'ar' ? 'إخفاء النص' : 'Hide Text'}
                </Label>
                <input
                  type="checkbox"
                  checked={isHidden}
                  onChange={(e) => setIsHidden(e.target.checked)}
                  className="w-5 h-5"
                />
              </div>

              {/* Preview */}
              <div>
                <Label className="mb-2 block">{i18n.language === 'ar' ? 'معاينة' : 'Preview'}</Label>
                <div 
                  className="p-4 border-2 border-dashed rounded-lg bg-white"
                  style={{
                    color: textColor,
                    fontSize: `${fontSize}px`,
                    opacity: isHidden ? 0.3 : 1
                  }}
                >
                  {editingText}
                </div>
              </div>

              {/* Location Info */}
              <div className="p-3 bg-blue-50 rounded text-sm">
                <p className="text-blue-900">
                  <strong>{i18n.language === 'ar' ? 'الموقع:' : 'Location:'}</strong> {selectedText.file}
                </p>
                <p className="text-blue-900">
                  <strong>{i18n.language === 'ar' ? 'السطر:' : 'Line:'}</strong> {selectedText.lineNumber}
                </p>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <Button variant="outline" onClick={() => setSelectedText(null)}>
                {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button onClick={saveChanges} className="gap-2">
                <Save className="w-4 h-4" />
                {i18n.language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && searchResults.length === 0 && searchQuery && (
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-600 mb-2">
            {i18n.language === 'ar' ? 'لم يتم العثور على نتائج' : 'No results found'}
          </h3>
          <p className="text-slate-500">
            {i18n.language === 'ar' 
              ? 'جرب البحث بكلمات مختلفة'
              : 'Try searching with different keywords'}
          </p>
        </div>
      )}
    </div>
  );
};

export default UniversalTextSearch;
